// Ogma Quote in the app (steps 9.16 to 9.19 and 9.27; docs/specs/CONNECT.md section 7, docs/specs/QUOTE.md): the price
// book's and the question flows' types, the words for a quote's states and why one waits, and the evaluator and the
// flow checker themselves. Neither is copied: they are the very files the "quote" Edge Function and the act function use
// (supabase/functions/quote/lib/evaluate.ts and flows.ts, allowed in vite.config.ts), so the test panel and the flow
// preview show exactly what a customer will get. lib/quotes.test.ts and lib/questionFlows.test.ts run the same fixtures
// as the Deno tests.
export {
  BILLINGS,
  checkDefinition,
  cleanAnswers,
  evaluate,
  forCustomer,
  formatMoney,
  isPriceDefinition,
  priceLine,
  type Answer,
  type Answers,
  type Bands,
  type Billing,
  type BillingTotal,
  type Condition,
  type ConditionOp,
  type CustomerOption,
  type Extra,
  type GroupItem,
  type OptionDef,
  type OptionResult,
  type PriceDefinition,
  type Product,
  type Question,
  type QuestionType,
  type QuoteResult,
  type Scalar,
  type Tier,
} from "../../../../supabase/functions/quote/lib/evaluate";
export { CONDITION_OPS } from "../../../../supabase/functions/quote/lib/evaluate";
import type { Answer, Answers, Billing, Condition, GroupItem, PriceDefinition, Question, QuoteResult, Scalar, Step } from "../../../../supabase/functions/quote/lib/evaluate";

export type QuoteStatus = "draft" | "approved" | "sent" | "viewed" | "accepted" | "paid" | "expired" | "declined" | "superseded" | "withdrawn";

/** A quote as crm_quotes and crm_quote return it (crm__quote_json, migration 20261129100000). */
export interface QuoteRow {
  id: string;
  label: string;
  number: number;
  revision: number;
  status: QuoteStatus;
  organisation_id: string;
  organisation?: string;
  person_id?: string;
  person?: string;
  email?: string;
  deal_id?: string;
  run_id?: string;
  price_version_id?: string;
  price_version?: number;
  version_seq?: number;
  book_id?: string;
  book?: string;
  response_id?: string;
  answers_status: "proposed" | "confirmed";
  outcome?: QuoteResult["outcome"];
  currency?: string;
  default_option?: string;
  total?: number;
  check_reasons: string[];
  priced_at?: string;
  approved_by?: string;
  approved_at?: string;
  decision_note?: string;
  valid_until?: string;
  sent_at?: string;
  viewed_at?: string;
  view_count?: number;
  accepted_at?: string;
  accepted_option?: string;
  accepted_total?: number;
  accepted_name?: string;
  declined_at?: string;
  declined_by?: "customer" | "person";
  decline_reason?: string;
  decline_note?: string;
  signing?: { provider?: string; status?: string; external_id?: string };
  page_token: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  // crm_quote only
  answers?: Answers;
  answers_sources?: Record<string, string>;
  result?: QuoteResult | null;
  questions?: Question[] | null;
  events?: { at: string; actor: string; step_id: string; kind: string; detail: Record<string, unknown> }[];
}

export interface PriceVersion {
  id: string;
  book_id?: string;
  version: number;
  status: "draft" | "published" | "retired";
  source: "person" | "upload" | "copy";
  note?: string;
  definition: PriceDefinition;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
  published_by?: string;
  published_at?: string;
  retired_at?: string;
  quotes: number;
  accepted: number;
}

/** One book's versions (crm_price_versions). */
export interface PriceBook {
  check_first: number;
  /** A price list a person uploaded is being read by the deal agent. */
  reading: boolean;
  book?: { id: string; key: string; name: string; is_default: boolean; archived_at?: string } | null;
  versions: PriceVersion[];
}

/** A price book in the business's list (crm_price_books). */
export interface PriceBookRow {
  id: string;
  key: string;
  name: string;
  description?: string;
  cta_id?: string;
  cta?: string;
  is_default: boolean;
  archived_at?: string;
  published?: { id: string; version: number; published_at: string; currency?: string };
  drafts: number;
  quotes: number;
  flows: number;
}

/** A knowledge entry (crm_quote_knowledge). */
export interface KnowledgeEntry {
  id: string;
  title: string;
  body: string;
  source?: string;
  book_id?: string;
  book?: string;
  flow_id?: string;
  flow?: string;
  approved: boolean;
  approved_by?: string;
  updated_by: string;
  updated_at: string;
}

/** 9.28 A question the quote assistant passed on, waiting for an answer (crm_quote_questions). */
/** What the owner's test box got back (act try_assistant): the answer, its entry, and why it was passed on. */
export interface AssistantTry {
  answer: string;
  entry: string | null;
  handoff: "unknown" | "offer" | "topic" | "limit" | null;
  /** The kill switch: off, no customer sees the assistant (the test box still answers). */
  enabled: boolean;
}

/** One question a customer asked the assistant this week, with its answer (crm_assistant_review). */
export interface AssistantReviewRow {
  response_id: string;
  at: string;
  q: string;
  a: string;
  entry?: string;
  handoff?: "unknown" | "offer" | "topic" | "limit";
  review?: "approved" | "flagged";
  flow?: string;
  organisation_id?: string;
  organisation?: string;
}

export interface QuoteQuestion {
  id: string;
  question: string;
  flow_id?: string;
  flow?: string;
  organisation_id?: string;
  organisation?: string;
  created_at: string;
}

export const STATUS_LABEL: Record<QuoteStatus, string> = {
  draft: "Draft",
  approved: "Approved, about to go",
  sent: "Sent",
  viewed: "Viewed",
  accepted: "Accepted",
  paid: "Paid",
  expired: "Expired",
  declined: "Declined",
  superseded: "Replaced by a new revision",
  withdrawn: "Withdrawn",
};

/** Why a priced quote waits for the owner (crm_price_quote, decision 4). */
export const WAIT_REASON: Record<string, string> = {
  first_20: "one of the first quotes from this price list, which you check",
  refer: "a refer rule sends it to you",
  decline: "a decline rule says not to quote",
  guardrail: "a guardrail is not met",
  kill_switch: "the kill switch is on",
  gate: "you check every quote (9.17 in your approval gates)",
  campaign: "its campaign checks every quote",
};

/** The customer's one-click reasons (crm__quote_decline_reasons; the quote function's DECLINE_REASONS). */
export const DECLINE_LABEL: Record<string, string> = {
  price: "The price",
  timing: "The timing isn't right",
  scope: "It isn't quite what they need",
  elsewhere: "Went with someone else",
  not_needed: "No longer needed",
  other: "Something else",
};

export const QUESTION_TYPES = ["number", "choice", "yes_no", "postcode", "date", "group"] as const;
export const TIERS = ["good", "better", "best"] as const;
export const TIER_LABEL: Record<string, string> = { good: "Good", better: "Better", best: "Best" };
export const BILLING_LABEL: Record<Billing, string> = { one_off: "One off", monthly: "Monthly", yearly: "Yearly" };
export const OUTCOME_LABEL: Record<string, string> = {
  priced: "Priced",
  refer: "Goes to you (refer)",
  decline: "Declined by a rule",
  needs_answers: "Can't be priced yet",
};

/** The address of a quote's private page (the asset host's quote.<domain>; quotes.page_url in the settings). */
export function quotePageUrl(token: string, base = "https://quote.ogmahub.com"): string {
  return `${base.replace(/\/+$/, "")}/${token}`;
}

/** Whether a quote is still open (one per firm: crm_start_quote refuses a second). */
export function isOpen(q: Pick<QuoteRow, "status">): boolean {
  return ["draft", "approved", "sent", "viewed"].includes(q.status);
}

/** The words for why a quote waits, joined. */
export function waitText(reasons: string[] | undefined): string {
  const r = (reasons ?? []).map((k) => WAIT_REASON[k] ?? k);
  return r.length ? r.join("; ") : "";
}

/** A small starting price book: one product, one question, one option, VAT, 30 days. Saved as a draft. */
export function blankDefinition(currency = "GBP"): PriceDefinition {
  return {
    currency,
    products: [{ key: "service", label: "Our service", unit: "job", price: 100 }],
    questions: [{ key: "quantity", label: "How many", type: "number", min: 1, max: 1000 }],
    tables: [],
    steps: [{ kind: "tax", label: "VAT", rate_pct: 20 }],
    options: [{ key: "standard", label: "Standard", tier: "better", default: true, lines: [{ product: "service", quantity: "quantity" }], included: [] }],
    extras: [],
    guardrails: { validity_days: 30 },
    refer: [],
    decline: [],
    samples: [],
  };
}

/** A typed answer from a form box: numbers as numbers, yes/no as true or false, empty as missing. */
export function readAnswer(q: { type: string }, raw: string): Scalar | undefined {
  const v = raw.trim();
  if (!v) return undefined;
  if (q.type === "number" || q.type === "money") return /^-?\d+(\.\d+)?$/.test(v.replace(/,/g, "")) ? Number(v.replace(/,/g, "")) : v;
  if (q.type === "yes_no") return v === "yes" ? true : v === "no" ? false : v;
  return v;
}

/** What a form box holds: its text, the picks of a multiple choice, or for a repeating group each item's boxes. */
export type FieldValue = string | string[] | Record<string, string>[];

/** An answer as the form box shows it (a group's items field by field). */
export function showAnswer(v: Answer | undefined): FieldValue {
  if (v === undefined || v === null) return "";
  if (Array.isArray(v)) return v.map((item) => Object.fromEntries(Object.entries(item).map(([k, x]) => [k, showAnswer(x) as string])));
  if (typeof v === "boolean") return v ? "yes" : "no";
  return String(v);
}

/** Answers from the form's text, dropping empty ones; a group's items keep their position. */
export function answersFrom(questions: Pick<Question, "key" | "type" | "fields">[], text: Record<string, FieldValue>): Answers {
  const out: Answers = {};
  for (const q of questions) {
    const t = text[q.key];
    if (q.type === "group") {
      if (!Array.isArray(t)) continue;
      out[q.key] = t.filter((item): item is Record<string, string> => typeof item === "object").map((item): GroupItem => {
        const a: GroupItem = {};
        for (const f of q.fields ?? []) {
          const v = readAnswer(f, item[f.key] ?? "");
          if (v !== undefined) a[f.key] = v;
        }
        return a;
      });
      continue;
    }
    const a = readAnswer(q, typeof t === "string" ? t : "");
    if (a !== undefined) out[q.key] = a;
  }
  return out;
}

/** Splits "a, b, c" or one per line into a clean list. */
export function listFrom(text: string): string[] {
  return text
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** A key from a label: "Inspection visit" -> "inspection_visit". */
export function keyFrom(label: string, taken: string[] = []): string {
  let base = label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").replace(/^[^a-z]+/, "").slice(0, 36) || "item";
  if (!/^[a-z]/.test(base)) base = `x_${base}`;
  let k = base;
  for (let i = 2; taken.includes(k); i++) k = `${base}_${i}`;
  return k;
}

// ------------------------------------------------------------------ keys an owner never sees (the price book)
// A product's or a question's key is how quotes, flows and formulas name it. It follows the name an owner gives it until
// the book is published with it (then quotes and question flows depend on it). Renaming one renames every use.

/** A formula with the name `from` read as `to` (a group's `from.field` included); other names untouched. */
export function renameInFormula(formula: string, from: string, to: string): string {
  return formula.replace(new RegExp(`(?<![A-Za-z0-9_.])${from}(?![A-Za-z0-9_])`, "g"), to);
}

const renameRule = (c: Condition, from: string, to: string): Condition => {
  if ("all" in c) return { all: c.all.map((x) => renameRule(x, from, to)) };
  if ("any" in c) return { any: c.any.map((x) => renameRule(x, from, to)) };
  if ("not" in c) return { not: renameRule(c.not, from, to) };
  return c.q === from ? { ...c, q: to } : c;
};

/** A price book with one question's key changed everywhere it is used: the term, rate tables, formulas, rules and samples. */
export function renamePriceQuestion(def: PriceDefinition, from: string, to: string): PriceDefinition {
  if (from === to) return def;
  const f = (s: string | undefined) => (s === undefined ? s : renameInFormula(s, from, to));
  const w = <T extends { when?: Condition }>(x: T): T => (x.when ? { ...x, when: renameRule(x.when, from, to) } : x);
  return {
    ...def,
    questions: def.questions.map((q) => (q.key === from ? { ...q, key: to } : q)),
    ...(def.term ? { term: def.term.question === from ? { question: to } : def.term } : {}),
    ...(def.tables ? { tables: def.tables.map((t) => ({ ...t, inputs: t.inputs.map((i) => (i === from ? to : i)) })) } : {}),
    ...(def.steps ? { steps: def.steps.map((s): Step => {
      switch (s.kind) {
        case "adjust":
          return w({ ...s, ...(s.add !== undefined ? { add: f(s.add) } : {}), ...(s.multiply !== undefined ? { multiply: f(s.multiply) } : {}) });
        case "discount":
          return w({ ...s, pct: renameInFormula(s.pct, from, to) });
        case "tax":
          return s;
        default:
          return w(s);
      }
    }) } : {}),
    options: def.options.map((o) => ({ ...o, lines: o.lines.map((l) => w({ ...l, quantity: f(l.quantity)! })) })),
    ...(def.extras ? { extras: def.extras.map((x) => (x.quantity === undefined ? x : { ...x, quantity: f(x.quantity) })) } : {}),
    ...(def.refer ? { refer: def.refer.map(w) } : {}),
    ...(def.decline ? { decline: def.decline.map(w) } : {}),
    ...(def.samples ? { samples: def.samples.map((s) => ({ ...s, answers: Object.fromEntries(Object.entries(s.answers).map(([k, v]) => [k === from ? to : k, v])) })) } : {}),
  };
}

/** A price book with one product's key changed in its options' lines and its extras. */
export function renameProduct(def: PriceDefinition, from: string, to: string): PriceDefinition {
  if (from === to) return def;
  return {
    ...def,
    products: def.products.map((p) => (p.key === from ? { ...p, key: to } : p)),
    options: def.options.map((o) => ({ ...o, lines: o.lines.map((l) => (l.product === from ? { ...l, product: to } : l)) })),
    ...(def.extras ? { extras: def.extras.map((x) => (x.product === from ? { ...x, product: to } : x)) } : {}),
  };
}

/** A product's or question's new name; its key follows the name unless the book was published with it. */
export function relabelPriceItem(def: PriceDefinition, kind: "products" | "questions", i: number, label: string, fixed: ReadonlySet<string>): PriceDefinition {
  const list = kind === "products" ? def.products : def.questions;
  const item = list[i];
  if (!item) return def;
  const named: PriceDefinition = { ...def, [kind]: list.map((x, j) => (j === i ? { ...x, label } : x)) };
  if (fixed.has(item.key) || !label.trim()) return named;
  const key = keyFrom(label, list.map((x) => x.key).filter((k) => k !== item.key));
  return kind === "products" ? renameProduct(named, item.key, key) : renamePriceQuestion(named, item.key, key);
}
